<script setup>
import { ref, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../auth/stores/auth-store';
import { useDark, useToggle } from '@vueuse/core';
import { usePermissions } from '../../auth/composables/permissions';
import { Offcanvas } from 'bootstrap';

const router = useRouter();

/**
 * Bootstrap toggler
 */
const toggler = useTemplateRef('toggler');

/**
 * Bootrap nav bar toggler
 */
const navbarOpen = ref(false);

/**
 * Auth
 */
const { getById, getFirebaseUser, signout } = useAuthStore();
const { authUser, loading, submitting, error } = storeToRefs(useAuthStore());

/**
 * Permissions
 */
const { isAllowed } = usePermissions();

/**
 * Dark mode data attribute
 */
const isDark = useDark({
  selector: 'html',
  attribute: 'data-bs-theme',
  valueDark: 'dark',
  valueLight: 'light',
});

const toggleDark = useToggle(isDark);

/**
 * Dark mode class attribute
 */
useDark({
  selector: 'html',
  attribute: 'class',
  valueDark: 'dark',
  valueLight: '',
});

/**
 * Get data
 */
const getData = async () => {
  await getFirebaseUser();
};

getData();

/**
 * Hide offcanvas
 */
const hideOffcanvas = () => {
  const inst = Offcanvas.getOrCreateInstance(
    document.getElementById('offcanvasNavbar'),
  );
  if (inst) {
    inst.hide();
  }
};

/**
 * Logout user from firebase
 * @returns {void}
 */
const logout = async () => {
  await signout();
  router.push({ name: 'home' });

  // Page refreshes when called immediatly
  // setTimeout(
  //   () => router.push({ name: 'home' }),
  //   100
  // );
};
</script>

<template>
  <nav class="navbar navbar-expand-md border-bottom py-0 d-flex">
    <div class="container">
      <RouterLink :to="{ name: 'home' }" class="navbar-brand">
        <img id="logo" class="rounded" src="../assets/logo.png" />
      </RouterLink>
      <button
        class="navbar-toggler border-0 shadow-none"
        type="button"
        data-bs-toggle="offcanvas"
        data-bs-target="#offcanvasNavbar"
        aria-controls="offcanvasNavbar"
        aria-label="Toggle navigation"
        ref="toggler"
      >
        <span class="navbar-toggler-icon"></span>
      </button>
      <div
        class="offcanvas offcanvas-end"
        tabindex="-1"
        id="offcanvasNavbar"
        aria-labelledby="offcanvasNavbarLabel"
      >
        <div class="offcanvas-header">
          <h5 class="offcanvas-title" id="offcanvasNavbarLabel">Menu</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>
        <div class="offcanvas-body">
          <ul
            class="navbar-nav justify-content-end flex-grow-1 align-items-center"
          >
            <li class="nav-item">
              <RouterLink
                :to="{ name: 'event.list' }"
                class="nav-link"
                @click="hideOffcanvas()"
              >
                Events
              </RouterLink>
            </li>
            <li class="nav-item">
              <RouterLink
                :to="{ name: 'league.list' }"
                class="nav-link"
                @click="hideOffcanvas()"
              >
                Leagues
              </RouterLink>
            </li>
            <li class="nav-item">
              <RouterLink
                :to="{ name: 'standing.list' }"
                class="nav-link"
                @click="hideOffcanvas()"
              >
                Standings
              </RouterLink>
            </li>
            <li class="nav-item">
              <RouterLink
                :to="{ name: 'user.list' }"
                class="nav-link"
                v-if="isAllowed('user.list')"
                @click="hideOffcanvas()"
              >
                Users
              </RouterLink>
            </li>
            <li class="nav-item">
              <RouterLink
                :to="{ name: 'help' }"
                class="nav-link"
                @click="hideOffcanvas()"
              >
                Help
              </RouterLink>
            </li>
            <li class="nav-item d-md-none">
              <RouterLink
                v-if="authUser"
                :to="{ name: 'account' }"
                class="nav-link"
                @click="hideOffcanvas()"
              >
                My Account
              </RouterLink>
              <RouterLink
                v-if="!authUser"
                :to="{ name: 'login' }"
                class="nav-link"
                @click="hideOffcanvas()"
              >
                Login
              </RouterLink>
            </li>
            <li class="nav-item d-md-none" v-if="!authUser">
              <RouterLink
                :to="{ name: 'signup' }"
                class="nav-link"
                @click="hideOffcanvas()"
              >
                Sign Up!
              </RouterLink>
            </li>
            <li class="nav-item d-md-none">
              <a v-if="authUser" class="nav-link" href="#" @click="logout()"
                >Logout</a
              >
            </li>
            <li class="nav-item dropdown d-none d-md-inline-block ms-auto">
              <a
                class="nav-link pb-1 pe-0"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span class="fa-stack fa-2x app-dropdown-toggle">
                  <i class="fas fa-circle fa-stack-2x"></i>
                  <i class="fas fa-user fa-stack-1x fa-inverse"></i>
                </span>
              </a>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <RouterLink
                    v-if="authUser?.uid"
                    :to="{
                      name: 'profile.detail',
                      params: { userId: authUser.uid },
                    }"
                    class="dropdown-item"
                  >
                    {{ authUser.displayName }}<br />
                    <span class="fs-7">{{ authUser.email }}</span>
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    v-if="authUser"
                    :to="{ name: 'account' }"
                    class="dropdown-item"
                  >
                    My Account
                  </RouterLink>
                  <RouterLink
                    v-if="!authUser"
                    :to="{ name: 'login' }"
                    class="dropdown-item"
                  >
                    Login
                  </RouterLink>
                </li>
                <li v-if="!authUser">
                  <RouterLink :to="{ name: 'signup' }" class="dropdown-item">
                    Sign Up!
                  </RouterLink>
                </li>
                <li>
                  <a
                    v-if="authUser"
                    class="dropdown-item"
                    href="#"
                    @click="logout()"
                    >Logout</a
                  >
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </nav>
</template>

<style>
#logo {
  height: 50px;
}
.app-dropdown-toggle {
  font-size: 1rem;
}
</style>
